import React from 'react';
import { Link } from 'react-router-dom';

import PageHero from '../components/PageHero';
import SectionHeader from '../components/SectionHeader';
import VideoCard from '../components/VideoCard';
import videos from '../data/videos';
import '../styles/pages/work.css';

import eascdiThumb from '../assets/demo-eascdi.png';
import dlcThumb from '../assets/demo-dlc.png';
import sccThumb from '../assets/demo-scc.png';
import housingThumb from '../assets/demo-housing.png';
import complianceThumb from '../assets/demo-compliance.jpg';

const demos = [
  {
    title: 'EASCDI',
    description: 'Educational analytics and student-centered data intelligence demo.',
    route: '/eascdi',
    image: eascdiThumb
  },
  {
    title: 'Distance Learning Classroom (DLC)',
    description: 'Interactive distance-learning platform demo for training and education teams.',
    route: '/dlc',
    image: dlcThumb
  },
  {
    title: 'Assessment Management Tool (SCC)',
    description: 'Assessment workflow and reporting demo for institutional quality teams.',
    route: '/scc',
    image: sccThumb
  },
  {
    title: 'Student Housing Case Study',
    description: 'Housing operations and digital workflow case-study demo.',
    route: '/case-studies/housing',
    image: housingThumb
  },
  {
    title: 'Zoning Compliance Engine',
    description: 'Automated zoning compliance workflow and reporting demo.',
    route: '/compliance',
    image: complianceThumb
  }
];

const pad = (n) => String(n).padStart(2, '0');

function Demos() {
  const trackDemoClick = (demo) => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'demo_click', {
        demo_name: demo.title,
        demo_route: demo.route
      });
    }
  };

  const trackVideoPlay = (video) => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'video_play', {
        video_title: video.title,
        video_id: video.youtubeId
      });
    }
  };

  return (
    <div className="work-page">
      <PageHero
        eyebrow="Live builds & walkthroughs"
        title={<>See it <em>working</em></>}
        lead="Explore our live product demos and video walkthroughs."
        crumbs={[{ to: '/', label: 'Home' }, { label: 'Demos' }]}
        actions={
          <nav className="work-jump" aria-label="Demos sections">
            <a className="btn btn-ink btn-lg" href="#live-demos">
              Live demos <span className="work-jump__count">{pad(demos.length)}</span>
            </a>
            <a className="btn btn-ghost btn-lg" href="#video-walkthroughs">
              Video walkthroughs <span className="work-jump__count">{pad(videos.length)}</span>
            </a>
          </nav>
        }
      />

      {/* Anchor ids sit on the sections (not the split headings) so #jumps land
          on a stable box that the motion layer never transforms. */}
      <section id="live-demos" className="section section--sand work-anchor" aria-labelledby="live-demos-title">
        <div className="container">
          <SectionHeader
            index="01"
            eyebrow="Click through it yourself"
            title={<>Live <em>demos</em></>}
            lead="Open a working build in a new tab and click through it yourself."
            id="live-demos-title"
          />

          <div className="work-demos">
            {demos.map((demo, i) => (
              <div
                className={`work-demos__item${i === 0 ? ' work-demos__item--feature' : ''}`}
                key={demo.route}
                data-reveal
                data-delay={i === 0 ? undefined : String(((i - 1) % 2) * 0.08)}
              >
                <article className={`work-demo stretch-host${i === 0 ? ' work-demo--feature' : ''}`}>
                  <div className="work-demo__thumb">
                    <img src={demo.image} alt={`${demo.title} thumbnail`} loading="lazy" />
                  </div>
                  <div className="work-demo__body">
                    <p className="work-demo__num">{pad(i + 1)} <span aria-hidden="true">/</span> Live demo</p>
                    <h3 className="work-demo__title">{demo.title}</h3>
                    <p className="work-demo__desc">{demo.description}</p>
                    <Link
                      to={demo.route}
                      className="link-arrow link-stretch"
                      data-cursor="Open"
                      onClick={() => trackDemoClick(demo)}
                    >
                      Open Demo <i className="fa-solid fa-arrow-right" aria-hidden="true" />
                    </Link>
                  </div>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="video-walkthroughs" className="section section--light work-anchor" aria-labelledby="video-walkthroughs-title">
        <div className="container">
          <SectionHeader
            index="02"
            eyebrow="Guided tours"
            title={<>Video <em>walkthroughs</em></>}
            lead="Short guided tours of what we build. Nothing loads from YouTube until you press play."
            id="video-walkthroughs-title"
          />

          <div className={`work-videos${videos.length % 2 ? ' work-videos--odd' : ''}`}>
            {videos.map((video, i) => (
              <div className="work-videos__item" key={video.youtubeId} data-reveal data-delay={String((i % 2) * 0.08)}>
                <VideoCard video={video} onPlay={trackVideoPlay} />
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default Demos;
